import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AddZonePill from './AddZonePill';
import type { AreaMappingRow, MappingFilter } from './types';

interface MappingLine {
  rowKey: string;
  areaId: string;
  zoneId: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface MappingViewProps {
  areaRows: AreaMappingRow[];
  zoneInventory: string[];
  unmappedZoneIds: Set<string>;
  filter: MappingFilter;
  isEditMode?: boolean;
  saving?: boolean;
  onAddZone?: (areaId: string, zoneId: string, floorId: string) => void;
  getAvailableZonesForArea?: (row: AreaMappingRow) => string[];
}

const AREA_COLORS = ['#3182ce', '#38a169', '#805ad5', '#dd6b20', '#d53f8c', '#319795'];

const compareZoneIds = (a: string, b: string) => {
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
  return a.localeCompare(b);
};

const boxBaseStyle: React.CSSProperties = {
  borderRadius: 10,
  border: '1px solid var(--border, #e2e8f0)',
  background: 'var(--surface, #fff)',
  padding: '12px 14px',
  minHeight: 56,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 2,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

function bezierPath(line: MappingLine) {
  const controlOffset = Math.max(40, (line.x2 - line.x1) * 0.45);
  return `M ${line.x1} ${line.y1} C ${line.x1 + controlOffset} ${line.y1}, ${line.x2 - controlOffset} ${line.y2}, ${line.x2} ${line.y2}`;
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'var(--surface-hover, #edf2f7)',
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 13,
};

const MappingView: React.FC<MappingViewProps> = ({
  areaRows,
  zoneInventory,
  unmappedZoneIds,
  filter,
  isEditMode = false,
  saving = false,
  onAddZone,
  getAvailableZonesForArea,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const areaRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const zoneRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [lines, setLines] = useState<MappingLine[]>([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  const visibleAreas = useMemo(() => {
    if (filter === 'unmapped_areas') {
      return areaRows.filter((row) => row.isUnmappedArea);
    }
    if (filter === 'unmapped_zones') {
      return areaRows.filter((row) => row.bmsZones.some((zoneId) => unmappedZoneIds.has(zoneId)));
    }
    return areaRows;
  }, [areaRows, filter, unmappedZoneIds]);

  const visibleZones = useMemo(() => {
    const assigned = new Set(areaRows.flatMap((row) => row.bmsZones));
    if (filter === 'unmapped_zones') {
      return [...unmappedZoneIds].sort(compareZoneIds);
    }
    if (filter === 'unmapped_areas') {
      return [...assigned].sort(compareZoneIds);
    }
    const inventory = zoneInventory.length > 0 ? zoneInventory : [...assigned];
    return [...new Set(inventory)].sort(compareZoneIds);
  }, [areaRows, filter, unmappedZoneIds, zoneInventory]);

  const areaColorByRowKey = useMemo(() => {
    const map = new Map<string, string>();
    visibleAreas.forEach((row, index) => {
      map.set(row.rowKey, AREA_COLORS[index % AREA_COLORS.length]);
    });
    return map;
  }, [visibleAreas]);

  const updateLines = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    setSvgSize({ width: containerRect.width, height: containerRect.height });

    const nextLines: MappingLine[] = [];
    for (const row of visibleAreas) {
      for (const zoneId of row.bmsZones) {
        if (!visibleZones.includes(zoneId)) continue;

        const areaEl = areaRefs.current.get(row.rowKey);
        const zoneEl = zoneRefs.current.get(zoneId);
        if (!areaEl || !zoneEl) continue;

        const areaRect = areaEl.getBoundingClientRect();
        const zoneRect = zoneEl.getBoundingClientRect();

        nextLines.push({
          rowKey: row.rowKey,
          areaId: row.areaId,
          zoneId,
          x1: areaRect.right - containerRect.left,
          y1: areaRect.top + areaRect.height / 2 - containerRect.top,
          x2: zoneRect.left - containerRect.left,
          y2: zoneRect.top + zoneRect.height / 2 - containerRect.top,
        });
      }
    }

    setLines(nextLines);
  }, [visibleAreas, visibleZones]);

  useLayoutEffect(() => {
    updateLines();
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(() => updateLines());
    observer.observe(container);
    window.addEventListener('resize', updateLines);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateLines);
    };
  }, [updateLines]);

  const setAreaRef = (rowKey: string) => (element: HTMLDivElement | null) => {
    if (element) {
      areaRefs.current.set(rowKey, element);
    } else {
      areaRefs.current.delete(rowKey);
    }
  };

  const setZoneRef = (zoneId: string) => (element: HTMLDivElement | null) => {
    if (element) {
      zoneRefs.current.set(zoneId, element);
    } else {
      zoneRefs.current.delete(zoneId);
    }
  };

  if (visibleAreas.length === 0 && visibleZones.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted, #718096)' }}>
        No mappings to display for this filter.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        minHeight: 320,
        padding: '8px 0',
      }}
    >
      <svg
        width={svgSize.width}
        height={svgSize.height}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
        aria-hidden="true"
      >
        {lines.map((line) => (
          <path
            key={`${line.rowKey}-${line.zoneId}`}
            d={bezierPath(line)}
            fill="none"
            stroke={areaColorByRowKey.get(line.rowKey) ?? '#a0aec0'}
            strokeWidth={2}
            strokeOpacity={0.65}
          />
        ))}
      </svg>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 1fr) minmax(80px, 120px) minmax(180px, 1fr)',
          gap: 16,
          alignItems: 'start',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--muted, #718096)' }}>
            Areas
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleAreas.map((row) => {
              const color = areaColorByRowKey.get(row.rowKey) ?? '#718096';
              return (
                <div
                  key={row.rowKey}
                  ref={setAreaRef(row.rowKey)}
                  style={{
                    ...boxBaseStyle,
                    borderLeft: `4px solid ${color}`,
                    background: row.isUnmappedArea ? '#fffff0' : boxBaseStyle.background,
                    borderColor: row.isUnmappedArea ? '#f6e05e' : 'var(--border, #e2e8f0)',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{row.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {row.areaId} · {row.floorName}
                  </div>
                  {row.isUnmappedArea && (
                    <div style={{ fontSize: 11, color: '#975a16', marginTop: 2 }}>No zones mapped</div>
                  )}
                  {row.bmsZones.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {row.bmsZones.map((zoneId) => (
                        <span key={zoneId} style={chipStyle}>
                          {zoneId}
                        </span>
                      ))}
                    </div>
                  )}
                  {isEditMode && onAddZone && getAvailableZonesForArea && (
                    <div style={{ marginTop: 8 }}>
                      <AddZonePill
                        areaId={row.areaId}
                        floorId={row.floorId}
                        availableZoneIds={getAvailableZonesForArea(row)}
                        saving={saving}
                        onAdd={onAddZone}
                        chipStyle={chipStyle}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ textAlign: 'center', paddingTop: 28, fontSize: 12, color: 'var(--muted, #718096)' }}>
          mappings
        </div>

        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--muted, #718096)' }}>
            BACnet zones
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleZones.map((zoneId) => {
              const isUnmapped = unmappedZoneIds.has(zoneId);
              const linkedAreas = visibleAreas.filter((row) => row.bmsZones.includes(zoneId));
              const lineColor = linkedAreas.length === 1 ? areaColorByRowKey.get(linkedAreas[0].rowKey) : '#718096';

              return (
                <div
                  key={zoneId}
                  ref={setZoneRef(zoneId)}
                  style={{
                    ...boxBaseStyle,
                    borderRight: `4px solid ${isUnmapped ? '#f6e05e' : lineColor ?? '#cbd5e0'}`,
                    background: isUnmapped ? '#fffff0' : boxBaseStyle.background,
                    borderColor: isUnmapped ? '#f6e05e' : 'var(--border, #e2e8f0)',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>Zone {zoneId}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {isUnmapped
                      ? 'Unassigned'
                      : linkedAreas.length
                        ? `→ ${linkedAreas.map((row) => `${row.areaId} (${row.floorName})`).join(', ')}`
                        : 'Not linked in current filter'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MappingView;
